import React, { useEffect, useState } from "react";
import Network from "../../utils/Network";

import { Assets } from "../../constants/assets";
import { CustomIcon } from "../../components/Icons";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import CountUp from "react-countup";
import { useSelector, useDispatch } from "react-redux";
import ThousandsSeparator from "../../utils/thousands-separator";
import Dialog from "../../components/Dialog";
import CircularProgress from "../../components/CircularProgress";
import useToast from "../../hooks/useToast";
import useQuests from "../../hooks/useQuests";
import LevelBadge from "../../components/LevelBadge";
import useArenaShop from "../../hooks/useArenaShop";
import ExpandableCardGrid from "../../components/Cards/ExpandableCardGrid";
import { arenaShopTourSteps } from "../../tours/arenaShopTour";
import TourProvider, { useTour } from "../../components/Tour/TourProvider";
import ArenaTour from "../../components/Tour/ArenaTour";
import { useAppTranslation } from "../../i18n/useAppTranslation";

type Props = {};

const requiredActions = {
  33: {
    icon: Assets.icons.bloodStone,
  },
  34: {
    icon: "/assets/images/icons/cleg.png",
  },
};

// Inner component that has access to the tour context
const ArenaShopContent = ({ 
  data, 
  loading, 
  userInfo, 
  actionLoading, 
  confirmDialog, 
  handleClaimReward, 
  setConfirmDialog 
}) => {
  const { t } = useAppTranslation(["common", "marketplace"]);
  // Get tour functions from context
  const { restartTour, startTour } = useTour();
  const dispatch = useDispatch();
  
  const getTimer = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) {
      return null;
    }
    const start = new Date(startTime);
    const end = new Date(endTime);
    if (Date.now() < start.getTime()) {
      return { time: start, title: t("marketplace:shop.countdowns.startsIn") };
    }
    if (Date.now() < end.getTime()) {
      return { time: end, title: t("marketplace:shop.countdowns.endsIn") };
    }
    if (Date.now() > end.getTime()) {
      return null;
    }
  };
  
  // Transform rewards data to card props
  const cardData = data?.rewards.map(reward => {
    const time = getTimer(reward.fromDate, reward.toDate);
    
    return {
      id: reward.id,
      title: reward.title,
      image: reward.icon,
      description: reward.description,
      shortDescription: reward.shortDescription,
      isPackage: reward.isPackage,
      level: reward.rewards[0]?.level,
      timer: time,
      badges: reward.availableQuantity > 0 ? [
        {
          text: t("marketplace:shop.badges.left", {
            count: reward.availableQuantity,
          }),
          position: "top-left"
        }
      ] : [],
      action: {
        icon: requiredActions[reward.requiredAction]?.icon,
        text: ThousandsSeparator(reward.requiredActionValue),
        disabled: !reward.isEnabled,
        onClick: (e) => {
          setConfirmDialog({
            open: true,
            msg: t("marketplace:shop.confirmClaim.message"),
            submit: () => handleClaimReward(reward.id),
            title: t("marketplace:shop.confirmClaim.title"),
          });
        }
      },
      LevelBadgeComponent: LevelBadge
    };
  }) || [];
  
  // Function to force start tour
  const forceStartTour = () => {
    // Reset the tour first
    dispatch({ 
      type: 'RESET_TOUR', 
      payload: 'arenaShop' 
    });
    
    // Set as active tour
    dispatch({ 
      type: 'SET_ACTIVE_TOUR', 
      payload: 'arenaShop' 
    });
    
    // Force run state to true
    dispatch({ 
      type: 'SET_TOUR_RUN_STATE', 
      payload: { tourId: 'arenaShop', running: true } 
    });
    
    // Now try the normal method as well
  };
  
  return (
    <div>
      <motion.div
        className="p-relative"
        initial={isMobile ? undefined : { opacity: 0, y: -110, zIndex: 10 }}
        animate={{ opacity: 1, y: 0, zIndex: 10 }}
        transition={{ duration: 0.3, delay: 0, zIndex: 10 }}
        exit={{ opacity: 0, y: 130 }}
      >
        <div
          style ={{ minHeight: 70, marginTop: -20 }}
          className="tab-bar gap-2 pt-md-3 pb-md-0 d-flex mb-4 py-4 pt-4 justify-content-between align-items-center flex-wrap "
        >
          <div className="d-flex gap-2 fs-sm-small  mt-2 mt-md-0 align-items-center">
            <CustomIcon icon="info" width={20} height={20} />
            {t("marketplace:shop.hints.arena")}
          </div>
          <div className="d-flex ms-auto align-items-center">
            <div className="status-frame d-flex align-items-center ms-3 ">
              <img
                width={35}
                height={40}
                style={{ marginLeft: -10 }}
                className="ms-n2"
                src={Assets.icons.energy}
              />
              <span
                className="d-block"
                style={{ fontSize: 18, marginRight: -15 }}
              >
                <CountUp
                  duration={1}
                  decimals={0}
                  separator=","
                  end={userInfo?.totalEnergy}
                />
              </span>
              <img
                width={30}
                height={30}
                className="opacity-0"
                src="/assets/images/buttons/add-button.png"
              />
            </div>
            <div className="status-frame d-flex align-items-center ms-3 ">
              <img
                width={35}
                height={40}
                style={{ marginLeft: -5 }}
                className="ms-n2"
                src={Assets.icons.bloodStone}
              />
              <span
                className="d-block"
                style={{ fontSize: 18, marginRight: -15 }}
              >
                <CountUp
                  duration={1}
                  decimals={0}
                  separator=","
                  end={userInfo?.totalBloodGem}
                />
              </span>
              <img
                width={20}
                height={30}
                className="opacity-0"
                src="/assets/images/buttons/add-button.png"
              />
            </div>

          </div>
        </div>
      </motion.div>
      
      <ExpandableCardGrid 
        cards={cardData as any} 
        style={{ maxWidth: 1170 }}
        className="mx-auto mt-3 mt-md-5 ExpandableCardGrid"
      />
      
      <Dialog
        loading={actionLoading}
        title={confirmDialog?.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={confirmDialog?.submit}
        open={confirmDialog?.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        {confirmDialog?.msg}
      </Dialog>
    </div>
  );
};

const ArenaShop = (props: Props) => {
  const axios = new Network();
  const { data, loading, getShopData } = useArenaShop();
  const [actionLoading, setActionLoading] = useState(false);
  const userInfo = useSelector((state: any) => state.userInfo);
  const { getMe } = useQuests();
  const openToast = useToast();
  
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    submit: null,
    title: "",
    msg: "",
  });

  const handleClaimReward = async (id: number) => {
    setActionLoading(true);
    const response = await axios.CallApi({
      url: "/quest/arenashops/claim",
      body: {
        QuestId: id,
      },
    });
    openToast(response);
    if (response.ok) {
      getShopData();
      getMe();
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }
    setActionLoading(false);
  };

  useEffect(() => {
    getShopData();
  }, []);

  if (loading)
    return (
      <div
        style={{ minHeight: 500 }}
        className="p-relative d-flex align-items-center justify-content-center"
      >
        <CircularProgress />
      </div>
    );
  
  return (
    <TourProvider
      tours={[{
        id: 'arenaShop',
        steps: arenaShopTourSteps,
        autoStart: false,
        showSkipButton: true,
        showProgress: true,
        showArrows: true,
        arrowSize: 40,
        forcedSteps: true,
        spotlightClicks: true,
        advanceOnTargetClick: true,
        advanceDelay: 200,
        dependencies: [loading, !data?.rewards, data?.rewards?.length === 0]
      }]}
      renderHelpButton={false}
    >
      
      <ArenaShopContent 
        data={data}
        loading={loading}
        userInfo={userInfo}
        actionLoading={actionLoading}
        confirmDialog={confirmDialog}
        handleClaimReward={handleClaimReward}
        setConfirmDialog={setConfirmDialog}
      />
    </TourProvider>
  );
};

export default ArenaShop;
